import {t} from "@lingui/core/macro";
import {StyleSheet, type CSSProperties} from "aphrodite";
import moment from "moment";
import * as React from "react";
import {useEffect, useRef} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import Link from "@khanacademy/wonder-blocks-link";
import {
    ModalDialog,
    ModalFooter,
    ModalHeader,
    ModalPanel,
} from "@khanacademy/wonder-blocks-modal";
import {color, sizing, spacing} from "@khanacademy/wonder-blocks-tokens";
import {Body, HeadingSmall} from "@khanacademy/wonder-blocks-typography";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";
import {ShimmerRectangle} from "@khan/component-shimmer";
import {useQuery} from "@khan/data-gql";

import ConversationView from "../conversation-view.tsx";
import MaybeResumeLink from "../maybe-resume-link.tsx";

import {GUIDE_TRANSCRIPT_QUERY} from "./guide-transcript-query.ts";

import externalLinkIcon from "@phosphor-icons/core/bold/arrow-square-out-bold.svg";

interface Props {
    transcriptId: string;
    relationship: "SELF" | "COACH" | "DISTRICT_ADMIN" | "OTHER";
    closeModal: () => void;
    hideResumeThreadLink?: boolean;
    systemChatStyle?: CSSProperties;
    userChatStyle?: CSSProperties;
}

const GuideTranscriptModal = ({
    transcriptId,
    relationship,
    closeModal,
    hideResumeThreadLink = false,
    systemChatStyle,
    userChatStyle,
}: Props): React.ReactElement => {
    const [result] = useQuery(GUIDE_TRANSCRIPT_QUERY, {
        variables: {transcriptId},
    });

    const thread =
        result.status === "success" ? result.data?.aiGuideThread : null;
    const title = thread
        ? thread.title ||
          t`Khanmigo chat from ${moment(thread.lastUpdatedAt).format("l, LT")}`
        : t`Khanmigo Chat`;

    // Generate a unique ID for the modal title for accessibility
    const dialogTitleId = React.useId();

    const publishAnalyticsEvent = usePublishAnalyticsEvent();
    const loggedOnMountRef = useRef(false);
    useEffect(() => {
        // We only want to log this event once, when the thread is
        // available.
        if (!thread) {
            return;
        }
        if (!loggedOnMountRef.current) {
            loggedOnMountRef.current = true;
            publishAnalyticsEvent({
                eventType: "AiGuideHistoryThreadSelected",
                eventSchemaVersion: 3,
                openedTranscriptThreadId: transcriptId,
                reviewerRelationship: relationship,
                isFlaggedByModeration: thread.flagged,
            });
        }
    }, [publishAnalyticsEvent, thread, transcriptId, relationship]);

    if (thread?.interactions.length) {
        thread.interactions.sort(
            (a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt),
        );
    }

    let contents: React.ReactNode = null;

    let displayTitle = true;
    if (result.status === "loading" || result.status === "no-data") {
        contents = <ConversationLoadingView numMessages={5} />;
        displayTitle = false;
    } else if (result.status !== "success" || !thread) {
        const href = "/about/privacy-policy";

        const linkComponent = (
            <Link
                href={href}
                // We set tabbing to -1 so users will be sent to the next
                // element in the tabbing order outside of this menu on tab.
                tabIndex={-1}
                target="_blank"
                endIcon={<PhosphorIcon icon={externalLinkIcon} size="small" />}
            >
                {t`Privacy Policy`}
            </Link>
        );
        contents = (
            <>
                <View style={styles.smallSpacing} />

                <View style={styles.noChatContainer}>
                    <HeadingSmall>
                        {t`This chat is no longer available.`}
                    </HeadingSmall>
                    <Body tag="p" style={styles.noChatBody}>
                        <span>
                            {t`It has been removed from chat history in accordance with our deletion practices`}
                            : &nbsp;
                            {linkComponent}
                        </span>
                    </Body>
                </View>
            </>
        );
        displayTitle = false;
    } else {
        contents = (
            <ConversationView
                thread={thread}
                systemChatStyle={systemChatStyle}
                userChatStyle={userChatStyle}
            />
        );
    }

    return (
        <ModalDialog aria-labelledby={dialogTitleId} style={styles.modal}>
            <ModalPanel
                closeButtonVisible={true}
                header={
                    displayTitle && (
                        // Future dev, if you come here looking how to fix the
                        // bottom border of the header being cut off by the
                        // chat bubbles, I've already filed a ticket for it.
                        // This is a Wonder Blocks issue.
                        // https://khanacademy.atlassian.net/browse/FEI-6666
                        <ModalHeader title={title} titleId={dialogTitleId} />
                    )
                }
                content={<View style={styles.modalContent}>{contents}</View>}
                footer={
                    <ModalFooter>
                        {!hideResumeThreadLink && thread && (
                            <MaybeResumeLink
                                thread={thread}
                                onClassroomExperience={true}
                                buttonKind="secondary"
                            />
                        )}
                        <View style={styles.mediumSpacing} />
                        <Button
                            kind="primary"
                            onClick={closeModal}
                        >{t`Close`}</Button>
                    </ModalFooter>
                }
            />
        </ModalDialog>
    );
};

function ConversationLoadingView({
    numMessages,
}: {
    numMessages: number;
}): React.ReactElement {
    return (
        <View
            aria-label="Loading conversation"
            role="status"
            aria-live="polite"
        >
            {/* Fake header */}
            <ShimmerRectangle
                width="75%"
                height={75}
                style={styles.shimmerRectangle}
            />
            {/* Fake summary */}
            <ShimmerRectangle
                width="100%"
                height={120}
                style={styles.shimmerRectangle}
            />
            <hr />
            {/* Fake conversation */}
            {new Array(numMessages).fill(undefined).map((_, i) => (
                <ShimmerRectangle
                    key={i}
                    // Randomize the size to make it look more like a
                    // conversation.
                    width={
                        i % 2
                            ? 10 + Math.floor(Math.random() * 40) + "%"
                            : // Khanmigo bubbles are a little wider than the
                              // users bubbles.
                              50 + Math.floor(Math.random() * 30) + "%"
                    }
                    height={35 + Math.floor(Math.random() * 90)}
                    style={[
                        styles.shimmerRectangle,
                        {
                            alignSelf: i % 2 ? "flex-end" : "flex-start",
                        },
                    ]}
                />
            ))}
        </View>
    );
}

const styles = StyleSheet.create({
    smallSpacing: {
        height: sizing.size_080,
    },
    mediumSpacing: {
        width: sizing.size_160,
    },
    modal: {
        height: "90vh",
        width: "90%",
        maxWidth: 1024,
    },
    modalContent: {
        padding: spacing.medium_16,
        height: "100%",
        overflowY: "auto",
    },
    noChatContainer: {
        alignItems: "center",
        background: color.offBlack8,
        // Default bottom-margin on a `<p>` tag is 22px.  We want a total
        // bottom-padding of 40px.  22px - 40px = 18px.  It was either this or
        // set the bottom-margin of the `<p>` to 0px.
        padding: "40px 0 18px",
        textAlign: "center",
        marginBlockEnd: spacing.xxLarge_48,
    },
    noChatBody: {
        display: "flex",
        marginBlockStart: spacing.xSmall_8,
    },
    shimmerRectangle: {
        marginBlockEnd: spacing.medium_16,
    },
});

export {GuideTranscriptModal};
